import { userPool } from "cognitoAuthConfig.ts/userPool";
import ecomInterceptor from "interceptors/ecomInterceptor";
import toast from "react-hot-toast";
import { clearCookies } from "services/cookieService";
import { logoutUser } from "store/slices/cognitoAuthSlice";
import { AppDispatch } from "store/Store";
import { extractErrorMessage } from "./ErrorHandlers";
import { Handle406Result } from "Interfaces/interface.auth";

export const handleSocialLogin = (
  provider: "Google",
  redirectPath?: string
) => {
  const redirect = process.env.REACT_APP_COGNITO_REDIRECT_URI;
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

  if (!redirect || !domain || !clientId) {
    toast.error("Configuration error. Please contact support.");
    console.error("Missing Cognito environment variables");
    return;
  }

  const redirectUri = encodeURIComponent(redirect);
  const state = encodeURIComponent(redirectPath || "/");
  const url = `https://${domain}/oauth2/authorize?identity_provider=${provider}&redirect_uri=${redirectUri}&response_type=code&client_id=${clientId}&scope=email+openid+profile+aws.cognito.signin.user.admin&state=${state}`;

  window.location.href = url;
};

export function handle403Redirect() {
  if (window.location.pathname.includes("/signin")) return;
  toast.error("Session expired. Please sign in again.");
  localStorage.removeItem("cart");
  localStorage.removeItem("enquiryIntent");
  clearCookies();
  const currentPath = window.location.pathname + window.location.search;
  const encodedState = encodeURIComponent(currentPath);
  window.location.href = `/signin?state=${encodedState}`;
}

export function handle401Redirect() {
  if (window.location.pathname.includes("/signin")) return;

  toast.error("Session expired. Please log in again.");
  localStorage.removeItem("cart");
  localStorage.removeItem("enquiryIntent");
  clearCookies();

  const currentPath = window.location.pathname + window.location.search;
  const encodedState = encodeURIComponent(currentPath);
  window.location.href = `/signin?state=${encodedState}`;
}

export function handleMockUpNavigation(
  product: any,
  attributesSelected?: Record<string, any>
): string {
  if (!product?.attributes || !attributesSelected) return "";

  const attributesSelectedLowerCase: Record<string, any> = {};
  Object.keys(attributesSelected).forEach((key) => {
    attributesSelectedLowerCase[key.toLowerCase()] = attributesSelected[key];
  });

  const filteredAndSorted = product.attributes
    .filter((attr: any) => {
      const attrNameLower = attr.name?.toLowerCase();
      return (
        attr.isImageChanging &&
        attrNameLower &&
        attributesSelectedLowerCase[attrNameLower]
      );
    })
    .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const selectedValues = filteredAndSorted.map((attr: any) => {
    const attrNameLower = attr.name?.toLowerCase();
    return attributesSelectedLowerCase[attrNameLower];
  });

  return `/mockup-generator/${
    product.identification.NPIN
  }-${selectedValues.join("-")}`;
}

export const handle406LoginError = async (
  error: any,
  userEmail: string
): Promise<Handle406Result> => {
  const errorCode = error?.response?.status;

  if (errorCode === 406) {
    try {
      await ecomInterceptor.post("/auth/send-otp", {
        otpType: "email",
        value: userEmail,
      });

      toast.success(
        "Verification OTP sent to your email. Please verify to continue."
      );

      return {
        shouldVerifyEmail: true,
        userEmail: userEmail,
        otpSent: true,
      };
    } catch (otpError: any) {
      console.error("Failed to send OTP:", otpError);
      const otpMessage = extractErrorMessage(otpError);
      toast.error(`Failed to send verification OTP: ${otpMessage}`);

      return {
        shouldVerifyEmail: true,
        userEmail: userEmail,
        otpSent: false,
        error: "Failed to send verification OTP",
      };
    }
  }

  return {
    shouldVerifyEmail: false,
  };
};

export const handleResendVerificationOTP = async (
  email: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await ecomInterceptor.post("/auth/send-otp", {
      otpType: "email",
      value: email,
    });

    toast.success("Verification OTP resent to your email");
    return { success: true };
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(`Failed to resend OTP: ${message}`);
    return { success: false, error: message };
  }
};

export const handleInvalidCognitoUser = (dispatch: AppDispatch) => {
  clearCookies();
  localStorage.clear();
  sessionStorage.clear();

  const user = userPool.getCurrentUser();
  user?.signOut();

  dispatch(logoutUser());

  window.location.href = "/";
};
